import React from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  List, ListItem, ListItemIcon, ListItemText, Stack, Typography, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import moment from "moment";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import BeachAccessRoundedIcon from "@mui/icons-material/BeachAccessRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import Langs, { Localize } from "../../app/lang/langs";

/**
 * "This arrêt lands on other absences — here is what will happen."
 *
 * An arrêt may coexist with any absence; a congé payé is the one the server acts on, and the
 * consequence is not reversible by the user, so it is spelled out before anything is written
 * rather than reported afterwards. The server refuses to save until `confirm_overlaps` comes
 * back, so this dialog is the gate, not a courtesy.
 *
 * The three outcomes, all decided by where the arrêt STARTS:
 *   CP_TRIMMED — arrêt starts first: the CP is postponed, its overlapping days credited back
 *   CP_REMOVED — same, but the arrêt covers the whole CP, so it disappears
 *   CP_KEPT    — mid-CP: the holiday stands and is consumed; the CPAM will not pay those days
 *
 * Presentational only: the caller owns the decision and re-submits.
 *
 * On a phone this goes full screen. It is the last thing seen before a leave balance moves,
 * the list can run to several rows, and a boxed dialog at 360px leaves each row about eight
 * words wide — the periods and the day counts then wrap into an unreadable block. Full screen
 * also pins the two buttons below the scrolling list instead of letting them drift off.
 *
 * @param {object} overlaps - the server's plan: { cp: [], others: [], days_credited, days_covered }
 */
export default function WorkStoppageOverlapDialog({ open, overlaps, saving, onConfirm, onCancel }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  if (!overlaps) return null;

  const cp = overlaps.cp || [];
  const others = overlaps.others || [];
  const creditedBack = overlaps.days_credited || 0;
  const notPaid = overlaps.days_covered || 0;

  // The API sends 'YYYY-MM-DD' for the CP plans and whatever the leaves table holds for the
  // coexisting absences (which can arrive as a full ISO timestamp). Both are shown the way
  // every other date in the app is shown.
  const day = (value) => (value ? moment(value).format("DD/MM/YYYY") : "—");
  const period = (from, to) => `${day(from)} → ${day(to)}`;

  /** "3 jours recrédités" / "1 jour recrédité" — separate keys, never a "(s)". */
  const countLabel = (count, key) => `${count} ${Localize(count === 1 ? `${key}_ONE` : `${key}_MANY`)}`;

  const cpLine = (plan) => {
    if (plan.kind === "CP_REMOVED") {
      return {
        icon: <EventBusyRoundedIcon color="warning" />,
        primary: Localize("WS_OVERLAP_CP_REMOVED"),
        before: period(plan.cp_from, plan.cp_to),
        after: null,
        detail: countLabel(plan.days_credited, "WS_OVERLAP_DAYS_CREDITED"),
      };
    }
    if (plan.kind === "CP_TRIMMED") {
      return {
        icon: <EventRepeatRoundedIcon color="warning" />,
        primary: Localize("WS_OVERLAP_CP_TRIMMED"),
        before: period(plan.cp_from, plan.cp_to),
        after: period(plan.new_cp_from, plan.new_cp_to),
        detail: countLabel(plan.days_credited, "WS_OVERLAP_DAYS_CREDITED"),
      };
    }
    return {
      icon: <BeachAccessRoundedIcon color="info" />,
      primary: Localize("WS_OVERLAP_CP_KEPT"),
      before: period(plan.cp_from, plan.cp_to),
      after: null,
      detail: countLabel(plan.days_covered, "WS_OVERLAP_DAYS_NOT_PAID"),
    };
  };

  // One fact per line rather than one long dash-joined run: at 360px the old single string
  // ("period → period — n days credited back") wrapped mid-date and the number, which is the
  // part that has to be defended afterwards, ended up buried in the middle of a paragraph.
  const detailLine = (text, strong) => (
    <Box
      component="span"
      sx={{
        display: "block",
        // Dates must never be split across lines; the day count may wrap freely.
        whiteSpace: strong ? "normal" : "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: strong ? 600 : 400,
        color: strong ? "text.primary" : "inherit",
      }}
    >
      {text}
    </Box>
  );

  const rowSx = { py: 0.75, alignItems: "flex-start" };
  const iconSx = { minWidth: 36, mt: 0.5 };
  // `secondary` renders a <p> by default, which cannot legally hold the block spans below.
  const textSlotProps = {
    primary: { sx: { fontWeight: 600, fontSize: "0.9rem", lineHeight: 1.35 } },
    secondary: { component: "div", sx: { fontSize: "0.8rem", lineHeight: 1.45 } },
  };

  // Touch targets, and a stacked footer on a phone: side by side, "Annuler" and
  // "Confirmer et enregistrer" do not fit across 360px without one of them wrapping.
  const actionSx = { textTransform: "none", minHeight: 44, width: { xs: "100%", sm: "auto" } };

  return (
    <Dialog
      open={!!open}
      onClose={saving ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: { xs: "1.05rem", sm: "1.25rem" }, lineHeight: 1.3, pr: 2 }}>
        <Langs str="WS_OVERLAP_TITLE" />
      </DialogTitle>

      {/* Dividers only when the footer is pinned to the bottom of the screen, so the list
          visibly scrolls under it instead of looking cut off. */}
      <DialogContent dividers={fullScreen} sx={{ px: { xs: 2, sm: 3 } }}>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            <Langs str="WS_OVERLAP_INTRO" />
          </Typography>

          {cp.length ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                <Langs str="WS_OVERLAP_CP_HEADING" />
              </Typography>
              <List dense disablePadding>
                {cp.map((plan) => {
                  const line = cpLine(plan);
                  return (
                    <ListItem key={plan.id} disableGutters sx={rowSx}>
                      <ListItemIcon sx={iconSx}>{line.icon}</ListItemIcon>
                      <ListItemText
                        primary={line.primary}
                        slotProps={textSlotProps}
                        secondary={(
                          <>
                            {detailLine(line.after
                              ? `${Localize("WS_OVERLAP_BEFORE")} ${line.before}`
                              : line.before)}
                            {line.after
                              ? detailLine(`${Localize("WS_OVERLAP_AFTER")} ${line.after}`)
                              : null}
                            {detailLine(line.detail, true)}
                          </>
                        )}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ) : null}

          {others.length ? (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                <Langs str="WS_OVERLAP_OTHERS_HEADING" />
              </Typography>
              <List dense disablePadding>
                {others.map((leave) => (
                  <ListItem key={leave.id} disableGutters sx={rowSx}>
                    <ListItemIcon sx={iconSx}><LayersRoundedIcon color="action" /></ListItemIcon>
                    <ListItemText
                      primary={leave.description || leave.type}
                      slotProps={textSlotProps}
                      secondary={detailLine(period(leave.start_date, leave.end_date))}
                    />
                  </ListItem>
                ))}
              </List>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                <Langs str="WS_OVERLAP_OTHERS_HINT" />
              </Typography>
            </Box>
          ) : null}

          {/* The two numbers anyone actually has to defend afterwards — one to the collab,
              one to the CPAM — so they are stated rather than left to be inferred. */}
          {creditedBack || notPaid ? (
            <Alert severity={creditedBack ? "warning" : "info"} sx={{ borderRadius: 2 }}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {creditedBack ? (
                  <Chip size="small" color="success" variant="outlined"
                    label={`+${countLabel(creditedBack, "WS_OVERLAP_CHIP_CREDITED")}`} />
                ) : null}
                {notPaid ? (
                  <Chip size="small" color="warning" variant="outlined"
                    label={countLabel(notPaid, "WS_OVERLAP_CHIP_NOT_PAID")} />
                ) : null}
              </Stack>
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions
        disableSpacing
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          gap: { xs: 1.25, sm: 1 },
          // Stacked on a phone, primary on top; side by side from sm up as before.
          flexDirection: { xs: "column-reverse", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "flex-end",
        }}
      >
        <Button onClick={onCancel} disabled={saving} sx={actionSx}>
          <Langs str="CANCEL" />
        </Button>
        <Button
          onClick={onConfirm}
          disabled={saving}
          variant="contained"
          // The caller keeps this dialog up while the save runs, so the tap has to show it
          // took — there is nothing else on screen to look at on a phone.
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            ...actionSx,
            fontWeight: 600,
            // MUI's warning.main under white text is about 3.2:1 — short of 4.5:1 for a
            // label this size, and this is the one button in the app that spends someone's
            // holiday. The deeper burnt orange keeps the warning reading and passes.
            ...(creditedBack
              ? {
                bgcolor: "#B25F00",
                "&:hover": { bgcolor: "#9A5200" },
              }
              : {}),
          }}
        >
          <Langs str="WS_OVERLAP_CONFIRM" />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
